// src/landings/components/resources/ArticleContent.tsx
import { motion } from "framer-motion";
import { Article } from "../../../data/resources";
import DOMPurify from "dompurify";
import { cn } from "../../../lib/utils";
import { useEffect } from "react";

interface ArticleContentProps {
  articles: Article[];
  setArticleRefs: (id: string, ref: HTMLDivElement | null) => void;
}

function ArticleContent({ articles, setArticleRefs }: ArticleContentProps) {
  // Enhanced HTML processing with custom styles
  useEffect(() => {
    // Function to enhance content after rendering
    const enhanceContent = () => {
      // Find all the articles in the DOM
      articles.forEach(article => {
        const articleElement = document.getElementById(article.id);
        if (!articleElement) return;
        
        // Process headings
        const headings = articleElement.querySelectorAll('h2, h3, h4');
        headings.forEach(heading => {
          heading.classList.add('font-bold', 'text-[#694e42]', 'mt-8', 'mb-4');
          
          if (heading.tagName === 'H3') {
            heading.classList.add('text-xl', 'border-b', 'border-[#e2dad3]', 'pb-2');
          }
          
          if (heading.tagName === 'H4') {
            heading.classList.add('text-lg');
          }
        });
        
        // Process paragraphs
        const paragraphs = articleElement.querySelectorAll('p');
        paragraphs.forEach(paragraph => {
          paragraph.classList.add('mb-4', 'leading-7', 'text-[#555]');
        });
        
        // Process lists
        const lists = articleElement.querySelectorAll('ul, ol');
        lists.forEach(list => {
          list.classList.add('my-4', 'space-y-2', 'pl-5');
          
          // Style list items
          const items = list.querySelectorAll('li');
          items.forEach(item => {
            // Convert dashes to proper bullets or numbering with styling
            const content = item.innerHTML;
            if (content.startsWith('- ')) {
              item.innerHTML = content.replace('- ', '');
              item.classList.add('relative', 'pl-2');
              
              // Create custom bullet
              const bullet = document.createElement('span');
              bullet.className = 'absolute left-[-1rem] top-[0.4rem] w-2 h-2 bg-[#ff8c42] rounded-full';
              item.prepend(bullet);
            }
          });
        });
        
        // Enhance bold and strong text
        const strongElements = articleElement.querySelectorAll('strong');
        strongElements.forEach(element => {
          element.classList.add('text-[#694e42]', 'font-semibold');
        });
        
        // Add visual separators for major sections
        const defaultCategories = articleElement.querySelector('h3:contains("Default Categories")');
        if (defaultCategories) {
          const divider = document.createElement('div');
          divider.className = 'w-full h-px bg-gradient-to-r from-[#ff8c42]/30 to-transparent my-8';
          defaultCategories.before(divider);
        }
        
        // Format category lists as cards
        const categoryItems = [
          "Priority", "Personal", "Work", "Newsletters", 
          "Promotions", "Social", "Updates", "Forums"
        ];
        
        categoryItems.forEach(category => {
          const categoryElement = articleElement.querySelector(`p:contains("${category} -")`);
          if (categoryElement) {
            const content = categoryElement.innerHTML;
            const parts = content.split(' - ');
            
            if (parts.length === 2) {
              const wrapper = document.createElement('div');
              wrapper.className = 'flex items-start gap-3 p-3 bg-[#fff5eb]/50 rounded-md border border-[#e2dad3]/50 mb-2';
              
              const categoryName = document.createElement('div');
              categoryName.className = 'font-medium text-[#694e42] min-w-24';
              categoryName.textContent = parts[0];
              
              const categoryDesc = document.createElement('div');
              categoryDesc.className = 'text-[#555]';
              categoryDesc.textContent = parts[1];
              
              wrapper.appendChild(categoryName);
              wrapper.appendChild(categoryDesc);
              
              categoryElement.replaceWith(wrapper);
            }
          }
        });
      });
    };
    
    // Apply enhancements after a short delay to ensure content is rendered
    const timer = setTimeout(enhanceContent, 100);
    return () => clearTimeout(timer);
  }, [articles]);

  // Process content before rendering
  const processContent = (content: string) => {
    // Add classes to HTML elements 
    let processed = content;
    
    // Replace dash lists with proper HTML lists
    processed = processed.replace(/^- (.+)$/gm, '<li>$1</li>');
    processed = processed.replace(/(<li>.+<\/li>\n)+/g, '<ul class="custom-list">$&</ul>');
    
    // Enhance headings
    processed = processed.replace(/<h3>(.+?)<\/h3>/g, '<h3 class="enhanced-heading">$1</h3>');
    
    return processed;
  };

  return (
    <div className={cn("lg:w-3/5")}>
      {articles.length > 0 ? (
        articles.map((article, index) => (
          <motion.div
            key={article.id}
            id={article.id}
            ref={(el: HTMLDivElement | null) => setArticleRefs(article.id, el)}
            className="mb-16 pb-12 bg-white rounded-xl border border-[#e2dad3] shadow-md last:mb-0 last:pb-12 overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }} 
          >
            {/* Article header with accent */}
            <div className="relative">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#ff8c42] to-[#946e5d]"></div>
              <div className="pt-8 px-8 md:px-10">
                <h2 className="text-2xl md:text-3xl font-bold mb-6 text-[#694e42] group flex items-center">
                  {article.title}
                  <a
                    href={`#${article.id}`}
                    className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-label="Direct link to this section"
                  >
                    <svg
                      className="w-5 h-5 text-[#ff8c42]"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                    </svg>
                  </a>
                </h2>
              </div>
            </div>

            {/* Article content with enhanced styling */}
            <div className="px-8 md:px-10">
              <div className="content-wrapper leading-relaxed text-[#555]">
                {article.content ? (
                  <div
                    className="
                      article-content
                      [&_h3]:text-xl
                      [&_h3]:font-bold
                      [&_h3]:text-[#694e42]
                      [&_h3]:mt-8
                      [&_h3]:mb-4
                      [&_h3]:pb-2
                      [&_h3]:border-b
                      [&_h3]:border-[#e2dad3]
                      
                      [&_h4]:text-lg
                      [&_h4]:font-semibold
                      [&_h4]:text-[#694e42]/90
                      [&_h4]:mt-6
                      [&_h4]:mb-3
                      
                      [&_p]:mb-4
                      [&_p]:leading-7
                      
                      [&_ul]:my-4
                      [&_ul]:pl-5
                      [&_ul]:space-y-2
                      
                      [&_ol]:my-4
                      [&_ol]:pl-5
                      [&_ol]:list-decimal
                      [&_ol]:space-y-2
                      
                      [&_li]:relative
                      [&_ul_li]:pl-2
                      [&_ul_li]:before:absolute
                      [&_ul_li]:before:content-['']
                      [&_ul_li]:before:left-[-1rem]
                      [&_ul_li]:before:top-[0.4rem]
                      [&_ul_li]:before:w-2
                      [&_ul_li]:before:h-2
                      [&_ul_li]:before:bg-[#ff8c42]
                      [&_ul_li]:before:rounded-full
                      
                      [&_strong]:text-[#694e42]
                      [&_strong]:font-semibold
                    "
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(article.content),
                    }}
                  />
                ) : (
                  <p>Content not available.</p>
                )}
              </div>
              
              {/* Article footer with author/metadata styling */}
              <div className="mt-12 pt-8 border-t border-[#e2dad3] text-sm text-[#8a7266] flex justify-between items-center">
                <div>
                  <span>Last updated: March 2025</span>
                </div>
                <div className="flex items-center gap-2">
                  <button className="hover:text-[#ff8c42] transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6.633 10.25c.806 0 1.533-.446 2.031-1.08a9.041 9.041 0 0 1 2.861-2.4c.723-.384 1.35-.956 1.653-1.715a4.498 4.498 0 0 0 .322-1.672V2.75a.75.75 0 0 1 .75-.75 2.25 2.25 0 0 1 2.25 2.25c0 1.152-.26 2.243-.723 3.218-.266.558.107 1.282.725 1.282m0 0h3.126c1.026 0 1.945.694 2.054 1.715.045.422.068.85.068 1.285a11.95 11.95 0 0 1-2.649 7.521c-.388.482-.987.729-1.605.729H13.48c-.483 0-.964-.078-1.423-.23l-3.114-1.04a4.501 4.501 0 0 0-1.423-.23H5.904m10.598-9.75H14.25M5.904 18.5c.083.205.173.405.27.602.197.4-.078.898-.523.898h-.908c-.889 0-1.713-.518-1.972-1.368a12 12 0 0 1-.521-3.507c0-1.553.295-3.036.831-4.398C3.387 9.953 4.167 9.5 5 9.5h1.053c.472 0 .745.556.5.96a8.958 8.958 0 0 0-1.302 4.665c0 1.194.232 2.333.654 3.375Z" />
                    </svg>
                  </button>
                  <span>Was this helpful?</span>
                </div>
              </div>
            </div>
          </motion.div>
        ))
      ) : (
        <div className="py-20 text-center bg-white rounded-xl border border-[#e2dad3] shadow-md">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#fff5eb] flex items-center justify-center text-[#ff8c42]">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-[#694e42] mb-2">
            No articles yet
          </h3>
          <p className="text-[#8a7266]">
            Check back soon for new content in this category.
          </p>
        </div>
      )}
    </div>
  );
}

export default ArticleContent;